/**
 * Orientation, as a rotation rather than as three angles.
 *
 * The flight loop keeps a heading as a yaw and a pitch, eased toward a target by
 * `stepTurn`. That model has a singularity straight up, which the cockpit avoids
 * by refusing to go there — so a ship can roll and yaw forever and cannot loop.
 * See `planning/six-degrees-plan.md`.
 *
 * This is the piece that replaces it: the same easing, the same feel, expressed
 * as a rotation that has no poles.
 *
 * ── Its own quaternions, and no three.js ────────────────────────────────────
 *
 * `@interspacial/shared` deliberately depends on nothing, and it is imported by
 * the server as well as the browser. Pulling a renderer in for four algebraic
 * identities would be the wrong trade — and these are the identities, in full,
 * short enough to read. Conventions match three.js exactly (`multiply` composes
 * a *local* rotation on the right) so the values pass straight into a scene
 * without a translation layer to get wrong.
 *
 * ── Why it eases rather than slerping ───────────────────────────────────────
 *
 * A plain slerp toward the target moves a fixed fraction of the way each frame,
 * which starts and stops instantly. The cockpit's whole sense of weight comes
 * from the *rate* being carried between frames and limited in how fast it may
 * change — the ship winds up into a turn and settles out of it, and the gap
 * between where it is pointing and where it has been asked to point is what
 * makes a heavy hull read as heavy.
 *
 * So this mirrors `stepTurn` beat for beat: a wanted rate proportional to the
 * error, clamped to the hull's turn rate, approached under an acceleration
 * limit. Same constants, same behaviour, one axis instead of two.
 */
/** No rotation at all. */
export const IDENTITY = { x: 0, y: 0, z: 0, w: 1 };
export function quatDot(a, b) {
    return a.x * b.x + a.y * b.y + a.z * b.z + a.w * b.w;
}
/**
 * Back to unit length.
 *
 * Composing rotations accumulates floating-point error, and an orientation that
 * has drifted off the unit sphere scales everything it is applied to. Cheap, and
 * the thing to do after any multiplication that will be kept.
 */
export function quatNormalise(q) {
    const length = Math.sqrt(quatDot(q, q));
    if (length === 0)
        return { ...IDENTITY };
    return { x: q.x / length, y: q.y / length, z: q.z / length, w: q.w / length };
}
/** The inverse of a unit rotation, which for unit length is the conjugate. */
export function quatConjugate(q) {
    return { x: -q.x, y: -q.y, z: -q.z, w: q.w };
}
/**
 * `a` then `b`, where `b` is expressed in `a`'s own frame.
 *
 * The order is the thing to get right and the thing that fails silently: this
 * composes `b` as a *local* rotation, so turning a rolled ship "up" turns it up
 * relative to its own wings rather than relative to the world. Swapping the
 * arguments gives a world-frame rotation instead, and the two are
 * indistinguishable until something is rolled.
 */
export function quatMultiply(a, b) {
    return {
        x: a.x * b.w + a.w * b.x + a.y * b.z - a.z * b.y,
        y: a.y * b.w + a.w * b.y + a.z * b.x - a.x * b.z,
        z: a.z * b.w + a.w * b.z + a.x * b.y - a.y * b.x,
        w: a.w * b.w - a.x * b.x - a.y * b.y - a.z * b.z,
    };
}
/** A rotation of `angle` radians about `axis`, which must be unit length. */
export function quatFromAxisAngle(axis, angle) {
    const half = angle / 2;
    const sin = Math.sin(half);
    return { x: axis.x * sin, y: axis.y * sin, z: axis.z * sin, w: Math.cos(half) };
}
/** Turn a direction by a rotation. `direction` need not be unit length. */
export function quatRotate(q, direction) {
    // The standard expansion of q * v * q⁻¹, without building either quaternion.
    const ix = q.w * direction.x + q.y * direction.z - q.z * direction.y;
    const iy = q.w * direction.y + q.z * direction.x - q.x * direction.z;
    const iz = q.w * direction.z + q.x * direction.y - q.y * direction.x;
    const iw = -q.x * direction.x - q.y * direction.y - q.z * direction.z;
    return {
        x: ix * q.w + iw * -q.x + iy * -q.z - iz * -q.y,
        y: iy * q.w + iw * -q.y + iz * -q.x - ix * -q.z,
        z: iz * q.w + iw * -q.z + ix * -q.y - iy * -q.x,
    };
}
/**
 * The rotation that takes `from` to `to`, expressed in `from`'s own frame.
 *
 * **Always the short way.** `q` and `−q` are the same orientation, so a relative
 * rotation can come out describing the long way round — 350° where 10° was
 * meant. Negating when the scalar part is negative picks the shorter of the two,
 * and it is the same trap already fixed once in `remoteShips`: nothing throws,
 * the ship simply takes the scenic route.
 */
export function quatDelta(from, to) {
    const delta = quatMultiply(quatConjugate(from), to);
    return delta.w < 0
        ? { x: -delta.x, y: -delta.y, z: -delta.z, w: -delta.w }
        : delta;
}
/** How far apart two orientations are, in radians. Never more than π. */
export function angleBetween(a, b) {
    const delta = quatDelta(a, b);
    // Clamped because a quaternion a hair off unit length can put `w` outside the
    // domain of `acos`, and `NaN` here would spread into the whole scene.
    return 2 * Math.acos(Math.min(1, Math.max(-1, delta.w)));
}
/**
 * The axis of a relative rotation, in the frame it was taken in.
 *
 * Null when there is nothing to rotate about — two orientations that already
 * agree have no axis, and inventing one would be inventing a direction to
 * wander in.
 */
export function axisOf(delta) {
    const length = Math.sqrt(delta.x * delta.x + delta.y * delta.y + delta.z * delta.z);
    if (length < 1e-9)
        return null;
    return { x: delta.x / length, y: delta.y / length, z: delta.z / length };
}
/**
 * How aggressively the wanted rate scales with the angle left to cover.
 *
 * The same value `stepTurn` uses, and deliberately so: this is meant to replace
 * that function without the ship feeling different. Kept here rather than
 * imported because the two will diverge if either is ever retuned, and a shared
 * constant would make that a silent change to both.
 */
const TURN_GAIN = 1.2;
/** How fast the turn rate itself may change, as a multiple of the turn rate. */
const TURN_RESPONSE = 0.9;
/**
 * Swing an orientation toward a target, easing in and out.
 *
 * The direct replacement for `stepTurn`, and it behaves the same way: the wanted
 * rate is proportional to how far there is to go, which produces the ease-out,
 * and the limit on how fast that rate may change produces the ease-in.
 *
 * ── One rate, not three ─────────────────────────────────────────────────────
 *
 * `stepTurn` runs twice with a rate each for yaw and pitch. This carries a
 * single rate about whichever axis the error is on, which is a real difference
 * when the target moves *sideways* mid-turn: the wind-up carries into the new
 * axis rather than starting again. That is the better behaviour — a ship already
 * swinging does not stop being a ship already swinging because the destination
 * moved — but it is a change, and it is the one to look at first if the handling
 * feels different after the swap.
 */
export function stepOrientation(state, target, handling, seconds) {
    const delta = quatDelta(state.orientation, target);
    const axis = axisOf(delta);
    // Already there. Held rather than nudged: without this an orientation that has
    // arrived would keep being multiplied by a near-identity rotation and drift.
    if (!axis)
        return { orientation: state.orientation, rate: 0 };
    const error = 2 * Math.acos(Math.min(1, Math.max(-1, delta.w)));
    const agility = handling.turnRate * TURN_RESPONSE;
    const wanted = Math.min(handling.turnRate, error * TURN_GAIN);
    const change = Math.max(-agility * seconds, Math.min(agility * seconds, wanted - state.rate));
    const rate = state.rate + change;
    const travelled = rate * seconds;
    /*
     * Settle rather than hunting.
     *
     * The same rule `stepTurn` applies, and for the same reason: without it the
     * last hair of a turn is chased forever and the hull rocks imperceptibly. Snap
     * once the remaining error is negligible and the rate has bled away.
     */
    if (error - travelled < 1e-4 && Math.abs(rate) < 1e-3) {
        return { orientation: quatNormalise(target), rate: 0 };
    }
    /*
     * Never past the target.
     *
     * The rate is carried between frames, so a hull that wound up hard into a long
     * turn can be asked to travel further than is left — and overshooting an
     * orientation reads as a twitch at the end of every large turn.
     */
    const step = Math.min(travelled, error);
    // Composed on the right, because `axis` is in the ship's own frame — see
    // `quatMultiply`. Normalised because this result is kept and composed again
    // next frame.
    const orientation = quatNormalise(quatMultiply(state.orientation, quatFromAxisAngle(axis, step)));
    return { orientation, rate };
}
/**
 * The rotation that points `forward` along a direction, keeping level.
 *
 * For the places that have a bearing rather than an orientation — cruise
 * steering at a destination, a jump run holding a heading, a new pilot dropped
 * facing a station. `up` is a hint rather than a constraint: it decides the roll
 * about the new heading, and is ignored when it lies along it.
 *
 * The ship's nose is `+Y` and its deck normal `+Z`, which is the frame the hull
 * mesh is built in.
 */
export function lookAlong(forward, up = { x: 0, y: 0, z: 1 }) {
    const f = normalise(forward);
    if (!f)
        return { ...IDENTITY };
    // Right-handed frame: right = forward × up, then a true up from those two.
    let right = normalise(cross(f, up));
    if (!right) {
        // Looking straight along the hint, so it says nothing about roll. Any
        // perpendicular will do and this one is always perpendicular to something.
        right = normalise(cross(f, Math.abs(f.z) > 0.9 ? { x: 1, y: 0, z: 0 } : { x: 0, y: 0, z: 1 }));
        if (!right)
            return { ...IDENTITY };
    }
    const deck = cross(right, f);
    /*
     * Built straight from the rotation matrix whose columns are those axes.
     *
     * The branch is the standard one: the trace is only well conditioned when it
     * is positive, and each of the other three cases divides by whichever diagonal
     * term is largest. Getting this wrong produces a rotation that is *nearly*
     * right, which is worse than one that is obviously wrong.
     */
    const m00 = right.x;
    const m01 = f.x;
    const m02 = deck.x;
    const m10 = right.y;
    const m11 = f.y;
    const m12 = deck.y;
    const m20 = right.z;
    const m21 = f.z;
    const m22 = deck.z;
    const trace = m00 + m11 + m22;
    if (trace > 0) {
        const s = 0.5 / Math.sqrt(trace + 1);
        return quatNormalise({
            w: 0.25 / s,
            x: (m21 - m12) * s,
            y: (m02 - m20) * s,
            z: (m10 - m01) * s,
        });
    }
    if (m00 > m11 && m00 > m22) {
        const s = 2 * Math.sqrt(1 + m00 - m11 - m22);
        return quatNormalise({
            w: (m21 - m12) / s,
            x: 0.25 * s,
            y: (m01 + m10) / s,
            z: (m02 + m20) / s,
        });
    }
    if (m11 > m22) {
        const s = 2 * Math.sqrt(1 + m11 - m00 - m22);
        return quatNormalise({
            w: (m02 - m20) / s,
            x: (m01 + m10) / s,
            y: 0.25 * s,
            z: (m12 + m21) / s,
        });
    }
    const s = 2 * Math.sqrt(1 + m22 - m00 - m11);
    return quatNormalise({
        w: (m10 - m01) / s,
        x: (m02 + m20) / s,
        y: (m12 + m21) / s,
        z: 0.25 * s,
    });
}
function cross(a, b) {
    return {
        x: a.y * b.z - a.z * b.y,
        y: a.z * b.x - a.x * b.z,
        z: a.x * b.y - a.y * b.x,
    };
}
function normalise(v) {
    const length = Math.hypot(v.x, v.y, v.z);
    if (length < 1e-9)
        return null;
    return { x: v.x / length, y: v.y / length, z: v.z / length };
}
/**
 * Rotate `from` toward `to`, by at most `maxRadians`.
 *
 * The blend the autopilots want: cruise nudges the target a fraction of the way
 * to a bearing each frame rather than snapping it, so a hand on the mouse moves
 * the ship and is then gently undone instead of being fought.
 *
 * Always the short way — see `quatDelta`.
 */
export function rotateToward(from, to, maxRadians) {
    const delta = quatDelta(from, to);
    const axis = axisOf(delta);
    if (!axis || maxRadians <= 0)
        return from;
    const error = 2 * Math.acos(Math.min(1, Math.max(-1, delta.w)));
    if (error <= maxRadians)
        return quatNormalise(to);
    return quatNormalise(quatMultiply(from, quatFromAxisAngle(axis, maxRadians)));
}
/**
 * Put the nose on a direction, and leave the roll alone.
 *
 * `lookAlong` builds an orientation from nothing and therefore has to *choose* a
 * roll, which is wrong for anything steering a ship that is already flying: an
 * autopilot taking a bearing should turn the hull onto it, not also decide which
 * way up the pilot is. This is the minimal rotation instead — the shortest arc
 * from where the nose is to where it should be, applied about the world.
 *
 * Composed on the *left*, because the correction is expressed in world terms
 * rather than in the ship's frame. That is the opposite of everywhere else in
 * this file and it is deliberate: the axis came from two world directions.
 */
export function pointNose(from, direction) {
    const target = unit(direction);
    if (!target)
        return from;
    const nose = unit(quatRotate(from, { x: 0, y: 1, z: 0 }));
    if (!nose)
        return from;
    const dot = Math.min(1, Math.max(-1, nose.x * target.x + nose.y * target.y + nose.z * target.z));
    // Already there, near enough that an axis would be noise.
    if (dot > 1 - 1e-12)
        return from;
    /*
     * Nose-on backwards. Every axis perpendicular to the nose is an equally good
     * half turn, so one is picked rather than dividing by a zero-length cross
     * product — which is `NaN`, and a `NaN` orientation takes the whole scene with
     * it.
     */
    if (dot < -1 + 1e-12) {
        const aside = Math.abs(nose.z) > 0.9 ? { x: 1, y: 0, z: 0 } : { x: 0, y: 0, z: 1 };
        const axis = unit(crossOf(nose, aside));
        if (!axis)
            return from;
        return quatNormalise(quatMultiply(quatFromAxisAngle(axis, Math.PI), from));
    }
    const axis = unit(crossOf(nose, target));
    if (!axis)
        return from;
    return quatNormalise(quatMultiply(quatFromAxisAngle(axis, Math.acos(dot)), from));
}
function crossOf(a, b) {
    return {
        x: a.y * b.z - a.z * b.y,
        y: a.z * b.x - a.x * b.z,
        z: a.x * b.y - a.y * b.x,
    };
}
function unit(v) {
    const length = Math.hypot(v.x, v.y, v.z);
    if (length < 1e-9)
        return null;
    return { x: v.x / length, y: v.y / length, z: v.z / length };
}
//# sourceMappingURL=orientation.js.map